import { addDays, format } from 'date-fns';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { TimesheetEntry } from '@/types/timesheet';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';
import { ALL_DAYS, isWeekendDay, type DayName } from '../timesheetGridUtils';
import { RowDailyNotePopover } from './RowDailyNotePopover';

export interface RowHoursCellsProps {
  entry: TimesheetEntry;
  index: number;
  visibleDays: readonly DayName[];
  currentWeekStart: Date;
  weekStartDay: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  nonWorkingDays: Set<string>;
  /** Uncommitted text per `${entryId}-${day}` cell key. */
  editingHours: Record<string, string | undefined>;
  /** Cell key of the daily note popover that is currently open, if any. */
  openNoteCell: string | null;
  draftNote: string;
  isReadOnly: boolean;
  isAbsence: boolean;
  isDaily: boolean;
  /** Name of the row's project, shown in the daily note popover header. */
  projectName: string | undefined;
  organizationSettings: OrganisationTimesheetSettings | undefined;
  onUpdateHours: (index: number, day: DayName, value: string) => void;
  onUpdateEntry: (index: number, field: keyof TimesheetEntry, value: unknown) => void;
  onSetEditingHours: (updater: (prev: Record<string, string | undefined>) => Record<string, string | undefined>) => void;
  onSetOpenNoteCell: (cellKey: string | null) => void;
  onSetDraftNote: (note: string) => void;
}

const getHoursCellKey = (entryId: number, day: DayName) => `${entryId}-${day}`;

/** Rounds and clamps a raw hours input according to organisation settings. */
function normaliseHours(raw: string, settings: OrganisationTimesheetSettings | undefined): number {
  let hours = raw === '' ? 0 : parseFloat(raw);
  if (isNaN(hours)) hours = 0;

  const timeRounding = settings?.timeRounding ?? 'none';
  let step = 0;
  if (timeRounding === '15min') step = 0.25;
  else if (timeRounding === '30min') step = 0.5;
  else if (timeRounding === '1hour') step = 1;
  if (step > 0) {
    hours = Math.round(hours / step) * step;
  }

  const minSetting = settings?.minimumEntryDuration ?? '6min';
  let min = 0.1; // 6min
  if (minSetting === '15min') min = 0.25;
  else if (minSetting === '30min') min = 0.5;
  else if (minSetting === '1hour') min = 1;
  if (hours !== 0 && Math.abs(hours) < min) {
    hours = hours < 0 ? -min : min;
  }

  const allowNeg = settings?.allowNegativeHours ?? false;
  return allowNeg ? Math.max(-24, Math.min(24, hours)) : Math.max(0, Math.min(24, hours));
}

/** Daily hours inputs (and their daily-note popovers) for a single timesheet row. */
export function RowHoursCells({
  entry,
  index,
  visibleDays,
  currentWeekStart,
  weekStartDay,
  nonWorkingDays,
  editingHours,
  openNoteCell,
  draftNote,
  isReadOnly,
  isAbsence,
  isDaily,
  projectName,
  organizationSettings,
  onUpdateHours,
  onUpdateEntry,
  onSetEditingHours,
  onSetOpenNoteCell,
  onSetDraftNote,
}: RowHoursCellsProps) {
  return (
    <>
      {visibleDays.map((day) => {
        const cellKey = getHoursCellKey(entry.id, day);
        const isEditing = editingHours[cellKey] !== undefined;
        const displayValue = isEditing
          ? editingHours[cellKey]!
          : entry.hours[day] === 0
            ? ''
            : entry.hours[day].toFixed(2);

        const existingNote = entry.dailyNotes?.[day] ?? '';
        const isRequired = organizationSettings?.requireDescription === 'always';
        // Derive display date for the note popover header
        const dayIndex = ALL_DAYS.indexOf(day);
        const daysFromStart = dayIndex >= weekStartDay
          ? dayIndex - weekStartDay
          : 7 - weekStartDay + dayIndex;
        const cellDate = addDays(currentWeekStart, daysFromStart);

        return (
          <td key={day} className={cn(
            'p-1',
            isWeekendDay(day) && 'bg-muted/30',
            nonWorkingDays.has(day) && 'bg-amber-50/40 dark:bg-amber-900/10',
          )}>
            <div className={cn("flex flex-row items-center gap-0.5", isDaily ? "w-full" : "")}>
              <Input
                data-tour="timesheet-hours-cell"
                type="text"
                inputMode="decimal"
                value={displayValue}
                onChange={(e) => {
                  if (!isAbsence) {
                    const value = e.target.value;
                    onSetEditingHours(prev => ({ ...prev, [cellKey]: value }));
                  }
                }}
                onFocus={(e) => {
                  // Select all text on focus for easy overwriting (Excel-like behavior)
                  e.target.select();
                }}
                onBlur={(e) => {
                  if (!isAbsence) {
                    const clamped = normaliseHours(e.target.value, organizationSettings);
                    onUpdateHours(index, day, String(clamped));
                    onSetEditingHours(prev => {
                      const next = { ...prev };
                      delete next[cellKey];
                      return next;
                    });
                    // Auto-open daily note popover when:
                    // - daily description mode is on
                    // - description is required (always)
                    // - hours were just entered (> 0)
                    // - there is no existing note for this day
                    // - the previous value was 0 (new entry, not editing existing)
                    const prevHours = entry.hours[day] ?? 0;
                    const hasExistingNote = existingNote.trim().length > 0;
                    if (isDaily && isRequired && clamped > 0 && prevHours === 0 && !hasExistingNote) {
                      // Reset draft so the new popover doesn't carry over a previous note
                      onSetDraftNote('');
                      // Small timeout so the input blur completes before the popover opens
                      setTimeout(() => onSetOpenNoteCell(cellKey), 50);
                    }
                  }
                }}
                onKeyDown={(e) => {
                  // Allow keyboard navigation like Excel
                  // Enter: commit value and blur (no native focus movement)
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                  // Tab: let the browser handle natively — onBlur fires
                  // automatically when focus moves to the next field.
                  // Calling blur() here breaks Firefox's sequential
                  // focus navigation.
                  // Allow arrow keys for increment/decrement
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    const current = parseFloat(displayValue) || 0;
                    const newValue = Math.min(24, current + 0.5);
                    onSetEditingHours(prev => ({ ...prev, [cellKey]: newValue.toFixed(1) }));
                  }
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    const current = parseFloat(displayValue) || 0;
                    const newValue = Math.max(0, current - 0.5);
                    onSetEditingHours(prev => ({ ...prev, [cellKey]: newValue.toFixed(1) }));
                  }
                }}
                placeholder={entry.hours[day] === 0 ? '0' : ''}
                className={cn(
                  "text-center text-sm h-9 px-1",
                  isDaily ? "flex-1 min-w-0" : "w-full",
                  "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                  entry.hours[day] > 0 ? "text-foreground" : "text-muted-foreground/40",
                  (isReadOnly || isAbsence) && "bg-muted/50 cursor-not-allowed",
                  !isReadOnly && !isAbsence && "hover:border-primary/50 hover:bg-accent/30 transition-colors cursor-text",
                  isAbsence && entry.hours[day] > 0 && "bg-purple-50/40 border-purple-200"
                )}
                disabled={isReadOnly || isAbsence}
              />
              {isDaily && isAbsence && <div className="w-5 h-5 shrink-0" />}
              {isDaily && !isAbsence && (entry.hours[day] > 0 ? (
                <RowDailyNotePopover
                  note={existingNote}
                  draftNote={draftNote}
                  isOpen={openNoteCell === cellKey}
                  isRequired={isRequired}
                  headerLabel={`${format(cellDate, 'EEEE d MMM')} · ${projectName || 'No project selected'}`}
                  onOpen={() => {
                    onSetOpenNoteCell(cellKey);
                    onSetDraftNote(existingNote);
                  }}
                  onDraftChange={onSetDraftNote}
                  onSave={() => onUpdateEntry(index, 'dailyNotes', { ...entry.dailyNotes, [day]: draftNote })}
                  onClose={() => onSetOpenNoteCell(null)}
                />
              ) : (
                <div className="w-5 h-5 shrink-0" />
              ))}
            </div>
          </td>
        );
      })}
    </>
  );
}
